import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Upload, FileText, FileSpreadsheet, FileImage, Trash2, Download, Loader2, FolderOpen, File, BookOpen } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import api from '../../lib/api'

const SURFACE = '#FFFFFF'
const BG      = '#F4F7FC'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'

function formatBytes(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(mimeType) {
  if (!mimeType) return File
  if (mimeType.includes('pdf'))        return FileText
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return FileSpreadsheet
  if (mimeType.startsWith('image/'))   return FileImage
  return FileText
}

const READABLE = /\.(docx?|pdf|txt)$/i // what the server can read text from

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function FilesPage() {
  const [files,     setFiles]     = useState([])
  const [loading,   setLoading]   = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error,     setError]     = useState('')
  const [dragOver,  setDragOver]  = useState(false)
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const [theses,  setTheses]  = useState(null)  // loaded the first time "Use in thesis" opens
  const [pickFor, setPickFor] = useState(null)  // file id with the picker open
  const [target,  setTarget]  = useState('')
  const [sending, setSending] = useState(false)

  const BUCKET = 'student-files'

  const load = async () => {
    try {
      setLoading(true)
      setError('')
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) { setFiles([]); return }

      const { data, error } = await supabase
        .from('user_files')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })

      if (error) {
        if (error.code === '42P01' || error.code === 'PGRST205') {
          setError('Files table not set up yet. Run migration 002_user_files.sql in Supabase.')
        } else {
          setError('Could not load files.')
        }
        setFiles([])
        return
      }

      const filesWithUrls = (data || []).map(f => {
        const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(f.storage_path || '')
        return { ...f, url: urlData?.publicUrl || null }
      })
      setFiles(filesWithUrls)
    } catch {
      setError('Could not load files.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (fileList) => {
    if (!fileList?.length) return
    setUploading(true)
    setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) throw new Error('Not authenticated')

      for (const file of Array.from(fileList)) {
        const uuid        = crypto.randomUUID()
        const safeName    = file.name.replace(/[^a-z0-9._-]/gi, '_')
        const storagePath = `${session.user.id}/${uuid}-${safeName}`

        const { error: uploadErr } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, file, { contentType: file.type, upsert: false })
        if (uploadErr) throw uploadErr

        const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath)

        const { error: dbErr } = await supabase.from('user_files').insert({
          user_id:      session.user.id,
          name:         file.name,
          storage_path: storagePath,
          size:         file.size,
          mime_type:    file.type,
        })
        if (dbErr && dbErr.code !== '42P01') {
          await supabase.storage.from(BUCKET).remove([storagePath])
          throw dbErr
        }

        setFiles(prev => [{
          id: uuid, user_id: session.user.id, name: file.name,
          storage_path: storagePath, size: file.size, mime_type: file.type,
          created_at: new Date().toISOString(), url: urlData?.publicUrl || null,
        }, ...prev])
      }
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const openPicker = async (fileId) => {
    setPickFor(pickFor === fileId ? null : fileId)
    if (theses) return
    try {
      const { data } = await api.get('/thesis')
      setTheses(data || [])
      if (data?.length === 1) setTarget(data[0].id)
    } catch {
      setTheses([])
      toast.error('Could not load your theses')
    }
  }

  // Download from storage, hand it to the same reader the thesis page uses, then open the Reference section.
  const sendToThesis = async (file) => {
    if (!target) return
    setSending(true)
    try {
      const { data: blob, error: dlErr } = await supabase.storage.from(BUCKET).download(file.storage_path)
      if (dlErr) throw dlErr
      const form = new FormData()
      form.append('file', new window.File([blob], file.name, { type: file.mime_type || blob.type }))
      form.append('source_type', 'reference')
      const { data } = await api.post(`/thesis/${target}/upload-source`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      toast.success(`${file.name}: read ${data.source.word_count?.toLocaleString() || ''} words`)
      navigate(`/app/thesis/${target}/chapters#reference-documents`)
    } catch (err) {
      toast.error(`${file.name}: ${err.response?.data?.error || err.message || 'could not read this file'}`)
    } finally {
      setSending(false)
    }
  }

  const handleDelete = async (fileId) => {
    const fileRecord = files.find(f => f.id === fileId)
    try {
      await supabase.from('user_files').delete().eq('id', fileId)
      if (fileRecord?.storage_path) {
        await supabase.storage.from(BUCKET).remove([fileRecord.storage_path])
      }
      setFiles(prev => prev.filter(f => f.id !== fileId))
    } catch {
      setError('Could not delete file.')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    upload(e.dataTransfer.files)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display" style={{ fontSize: '2rem', color: TEXT }}>My Files</h1>
          <p className="font-sans text-sm mt-1" style={{ color: FAINT }}>
            Upload PDFs, Word docs, or any supporting material. Use "Use in thesis" on a DOCX, DOC or PDF to have it read and cited in a thesis.
          </p>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-60"
          style={{ background: GOLD, color: '#FFFFFF' }}
          onMouseEnter={e => !uploading && (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
        >
          {uploading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Upload style={{ width: 15, height: 15 }} />}
          Upload File
        </button>
        <input ref={inputRef} type="file" className="hidden" multiple onChange={e => upload(e.target.files)} />
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl text-sm font-sans" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          {error}
        </div>
      )}

      {/* Drop zone */}
      <div
        className="rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors cursor-pointer"
        style={{
          borderColor: dragOver ? GOLD : BORDER,
          background: dragOver ? 'rgba(37,99,235,0.04)' : BG,
        }}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <Upload style={{ width: 28, height: 28, color: dragOver ? GOLD : FAINT, marginBottom: 10 }} />
        <p className="font-sans font-medium text-sm" style={{ color: dragOver ? GOLD : MUTED }}>
          Drop files here or click to browse
        </p>
        <p className="font-sans text-xs mt-1" style={{ color: FAINT }}>PDF, DOCX, TXT, XLSX — up to 50 MB each</p>
      </div>

      {/* File list */}
      <div className="rounded-2xl shadow-card overflow-hidden" style={{ background: SURFACE, border: `1px solid ${BORDER}` }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: TEXT }}>Uploaded Files</h2>
          {!loading && (
            <span className="font-mono text-xs" style={{ color: FAINT }}>{files.length} file{files.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 style={{ width: 24, height: 24, color: FAINT, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-14">
            <FolderOpen style={{ width: 36, height: 36, color: FAINT, margin: '0 auto 12px', opacity: 0.5 }} />
            <p className="font-sans text-sm font-medium" style={{ color: MUTED }}>No files yet</p>
            <p className="font-sans text-xs mt-1" style={{ color: FAINT }}>Upload a file above to get started.</p>
          </div>
        ) : (
          <div className="divide-y" style={{ '--tw-divide-color': BORDER }}>
            {files.map(file => (
              <div key={file.id} className="flex flex-wrap items-center gap-4 px-6 py-3.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(37,99,235,0.08)' }}>
                  {(() => { const Icon = fileIcon(file.mime_type); return <Icon style={{ width: 16, height: 16, color: GOLD }} /> })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm font-medium truncate" style={{ color: TEXT }}>{file.name}</p>
                  <p className="font-mono text-xs mt-0.5" style={{ color: FAINT }}>
                    {formatBytes(file.size)} · {formatDate(file.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {READABLE.test(file.name) && (
                    <button
                      onClick={() => openPicker(file.id)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-sans text-xs font-semibold"
                      style={{ background: 'rgba(37,99,235,0.08)', color: GOLD }}
                    >
                      <BookOpen style={{ width: 13, height: 13 }} /> Use in thesis
                    </button>
                  )}
                  {file.url && (
                    <a href={file.url} download={file.name} target="_blank" rel="noreferrer"
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: FAINT }}
                      onMouseEnter={e => e.currentTarget.style.color = TEXT}
                      onMouseLeave={e => e.currentTarget.style.color = FAINT}>
                      <Download style={{ width: 14, height: 14 }} />
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: FAINT }}
                    onMouseEnter={e => e.currentTarget.style.color = '#DC2626'}
                    onMouseLeave={e => e.currentTarget.style.color = FAINT}>
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>
                {pickFor === file.id && (
                  <div className="basis-full flex flex-wrap items-center gap-2 pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
                    {theses === null ? (
                      <Loader2 style={{ width: 14, height: 14, color: FAINT, animation: 'spin 1s linear infinite' }} />
                    ) : theses.length === 0 ? (
                      <p className="font-sans text-xs" style={{ color: MUTED }}>You have no thesis yet. Create one first.</p>
                    ) : (
                      <>
                        <select
                          value={target} onChange={e => setTarget(e.target.value)}
                          className="font-sans text-xs rounded-lg px-2 py-1.5"
                          style={{ border: `1px solid ${BORDER}`, background: SURFACE, color: TEXT, maxWidth: 320 }}
                        >
                          <option value="">Choose a thesis…</option>
                          {theses.map(t => <option key={t.id} value={t.id}>{t.title || 'Untitled thesis'}</option>)}
                        </select>
                        <button
                          onClick={() => sendToThesis(file)} disabled={!target || sending}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-semibold disabled:opacity-60"
                          style={{ background: GOLD, color: '#fff' }}
                        >
                          {sending && <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />}
                          {sending ? 'Reading…' : 'Read it and open Reference section'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
